import React, { useRef, useState, useEffect, useCallback } from "react";
import styles from "./PageHeader.module.scss";
import classnames from "classnames";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AutocompleteInput from "@src/components/common/AutocompleteInput";
import Menu, { MenuItem, MenuThemes } from "@src/components/common/Menu";
import { MenuController } from "@src/types/menu";
import UserDisplay from "@src/components/common/UserDisplay";
import * as AuthApi from "@src/api/AuthApi";
import ShopMegaMenuLinks from "./ShopMegaMenuLinks";
import { addStaticRootToUrl } from "@src/utils/static";
import MaxWidthContainer from "@src/components/containers/MaxWidthContainer";
import isOnPage, { A_CART_SUMMARY_PAGE } from "@src/utils/isOnPage";
import NumberBubble from "@src/components/common/NumberBubble";
import { useQuery } from "@src/hooks/useQuery";
import RequiresPermissionContainer from "@src/components/containers/RequiresPermissionContainer";
import Permissions from "@src/constants/Permissions";
import PermissionLevels from "@src/constants/PermissionLevels";
import NotificationBell from "@src/components/common/NotificationBell";
import { useCurrentUserStore } from "@src/state/currentUserState";
import { useCartStore } from "@src/state/cartState";
import RequiresRoleContainer from "@src/components/containers/RequiresRoleContainer";
import Roles from "@src/constants/Roles";

const SCROLL_DIRECTION_UP = "SCROLL_DIRECTION_UP";
const SCROLL_DIRECTION_DOWN = "SCROLL_DIRECTION_DOWN";

export default function PageHeader() {
  const { query } = useQuery();
  const [searchValue, setSearchValue] = useState<string>(
    query.get("term") || "",
  );
  const [lastScrollTop, _setLastScrollTop] = useState<number>(0);
  const [lastScrollDirection, _setLastScrollDirection] =
    useState<string>(SCROLL_DIRECTION_UP);
  const [shopMenuController, setShopMenuController] =
    useState<MenuController | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const lastScrollTopRef = useRef<number>(lastScrollTop);
  const lastScrollDirectionRef = useRef<string>(lastScrollDirection);

  const { currentUser } = useCurrentUserStore();
  const { cartCount, error: cartLoadError } = useCartStore();
  const isCartSummaryOpen =
    isOnPage(A_CART_SUMMARY_PAGE, location.pathname) &&
    !!cartCount &&
    cartCount > 0;

  const setLastScrollTop = (value: number) => {
    _setLastScrollTop(value);
    lastScrollTopRef.current = value;
  };

  const setLastScrollDirection = (value: string) => {
    _setLastScrollDirection(value);
    lastScrollDirectionRef.current = value;
  };

  const displayCount = cartLoadError || !cartCount ? 0 : cartCount;

  const handleLogout = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const response = await AuthApi.logoutUser();
    if (response.status === 200) {
      navigate(0);
    }
  };

  const handleScroll = useCallback(() => {
    const currentScrollTop = document.body.scrollTop;

    if (currentScrollTop !== lastScrollTopRef.current) {
      if (
        currentScrollTop > lastScrollTopRef.current &&
        lastScrollDirectionRef.current !== SCROLL_DIRECTION_DOWN
      ) {
        setLastScrollDirection(SCROLL_DIRECTION_DOWN);
      } else if (
        currentScrollTop < lastScrollTopRef.current &&
        lastScrollDirectionRef.current !== SCROLL_DIRECTION_UP
      ) {
        setLastScrollDirection(SCROLL_DIRECTION_UP);
      }
      setLastScrollTop(currentScrollTop <= 0 ? 0 : currentScrollTop);
    }
  }, []);

  useEffect(() => {
    document.body.addEventListener("scroll", handleScroll);
    return () => document.body.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSearch = (searchTerm?: string) => {
    const termToSearch = searchTerm || searchValue;
    const searchQuerystring = !!termToSearch
      ? `?term=${encodeURIComponent(termToSearch.trim())}`
      : "";
    navigate(`/search${searchQuerystring}`);
  };

  return (
    <MaxWidthContainer
      className={classnames(styles.root, {
        [styles.hidden]: lastScrollDirection === SCROLL_DIRECTION_DOWN,
      })}
    >
      <header className="flex-row flex-center space-evenly">
        <Link className={styles.homepageLink} to="/">
          <img
            src={addStaticRootToUrl("images/logo_colored.png")}
            alt="OrderEasy Logo"
          />
        </Link>

        <div
          className={classnames(styles.navigationItems, "spaced-content-lg")}
        >
          <RequiresPermissionContainer
            permission={{
              slug: Permissions.Products,
              level: PermissionLevels.View,
            }}
          >
            <AutocompleteInput
              width="40%"
              placeholder="Search"
              value={searchValue}
              onChange={setSearchValue}
              onSearch={handleSearch}
              limit={5}
              data-testid="page-header-search-input"
            />
          </RequiresPermissionContainer>

          <RequiresPermissionContainer
            permission={{
              slug: Permissions.ProductCategories,
              level: PermissionLevels.View,
            }}
          >
            <Menu
              buttonContent={
                <div
                  id="page-header-shop-menu-button"
                  data-testid="page-header-shop-menu-button"
                  className={styles.headerLink}
                >
                  Shop
                </div>
              }
              theme={MenuThemes.Mega}
              variations={{ dark: true, isSidebarShowing: isCartSummaryOpen }}
              onReceiveController={setShopMenuController}
            >
              <ShopMegaMenuLinks
                onLinkClicked={() => shopMenuController?.setIsOpen(false)}
              />
            </Menu>
          </RequiresPermissionContainer>

          <RequiresPermissionContainer
            permission={{
              slug: Permissions.Orders,
              level: PermissionLevels.View,
            }}
          >
            <Link
              id="page-header-orders-header-link"
              data-testid="page-header-orders-header-link"
              className={styles.headerLink}
              to="/orders"
            >
              Orders
            </Link>
          </RequiresPermissionContainer>
        </div>
        <div
          className={classnames(styles.actionDropdowns, "spaced-content-xxl")}
        >
          <NotificationBell className={styles.dropdownButton} />

          <RequiresRoleContainer role={Roles.ClinicUser}>
            <Link
              id="page-header-cart-header-link"
              data-testid="page-header-cart-header-link"
              className={styles.dropdownButton}
              to="/cart"
            >
              <i className="fa-light fa-cart-shopping" />
              {displayCount > 0 && (
                <span className={styles.cartBadgeContainer}>
                  <NumberBubble value={displayCount} />
                </span>
              )}
            </Link>
          </RequiresRoleContainer>

          <Menu
            buttonContent={
              <div
                id="page-header-user-menu-button"
                data-testid="page-header-user-menu-button"
                className={styles.dropdownButton}
              >
                <i className="fa-light fa-user" />
              </div>
            }
            theme={MenuThemes.Options}
          >
            <MenuItem viewOnly>
              <UserDisplay
                initialUserDisplayInfo={currentUser}
                isForCurrentUser
              />
            </MenuItem>
            <MenuItem>
              <Link
                id="page-header-account-settings-link"
                data-testid="page-header-account-settings-link"
                to="/account/general"
              >
                Account Settings
              </Link>
            </MenuItem>
            {/* Note: Make sure any new menu items have an id, for Pendo tagging */}
            {/* Note: Commented out for Beta - messaging function is hidden, beta will not have messaging */}
            {/* <MenuItem>
              <Link to="/messages">Messages</Link>
            </MenuItem> */}
            {/* Note: Commented out for Beta due to missing functionality */}
            {/* <MenuItem>
              <Link to="/manage-team">Manage Team</Link>
            </MenuItem>
            <MenuItem>
              <Link to="/support">Support</Link>
            </MenuItem> */}
            <MenuItem>
              <a
                id="logout-link"
                data-testid="page-header-logout-link"
                href="/logout"
                onClick={handleLogout}
              >
                Log Out
              </a>
            </MenuItem>
          </Menu>
        </div>
      </header>
    </MaxWidthContainer>
  );
}
